import { call, put, takeLatest, select } from 'redux-saga/effects';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isEqual from 'lodash/isEqual';
import { arrayMove } from '@dnd-kit/sortable';

import * as leadsApi from 'api/leads';
import { deleteDocument as deleteDocumentApi } from 'api/documents';
import * as leadRecordApi from 'api/lead-record';
import {
  prepareLeadForApi,
  prepareChecklistItemForApi,
} from 'utils/leads';
import { pushSiteError, push404Error } from 'containers/App/actions';
import * as apollo from 'apollo/leads.query';
import { objToCamelCase, objToSnake } from 'utils/general';
import { DELETE_LEAD_SUCCESS } from 'containers/Leads/types';
import { REMOVE_EVENT } from 'containers/Events/types';
import { DELETE_TASK_SUCCESS } from 'containers/App/constants';
import * as types from './types';

dayjs.extend(utc);

function* fetchLead(action) {
  try {
    const { orgId, leadId } = action.payload || {};
    let leadData;

    if (orgId && leadId) {
      const { data, ok, status } = yield call(leadsApi.getLead, orgId, leadId);
      if (ok && data) {
        leadData = objToCamelCase(data);
      } else if (status === 404) {
        yield put(push404Error());
        return;
      }
    }

    if (leadData) {
      const { lead, ...rest } = leadData;
      const { leadUserDefinedFields, ...leadRecord } = lead || {};
      yield put({
        type: types.ADD_LEAD,
        payload: { id: leadId, data: { ...rest, lead: leadRecord } },
      });

      if (leadUserDefinedFields) {
        yield put({
          type: types.ADD_CUSTOM_FORM_FIELD_DATA,
          payload: { id: leadId, data: leadUserDefinedFields },
        });
      }

    } else {
      yield put(
        pushSiteError({
          message: "Sorry, we couldn't load your lead. Please try again.",
        }),
      );
    }
  } catch (err) {
    yield put(
      pushSiteError({
        message: "Sorry, we couldn't load your lead. Please try again.",
      }),
    );
  }
}

function* updateLead(action) {
  const {
    orgId,
    leadId,
    updateData,
    emailUpdate,
    phoneUpdate,
    addressUpdate,
    websiteUpdate,
  } = action.payload || {};
  if (!orgId || !leadId || !updateData) return;

  const prevLead = yield select(state => state.leads.leadsById?.[leadId]);
  if (!prevLead) {
    yield put(
      pushSiteError({
        message: "Sorry, we couldn't update your lead. Please try again.",
      }),
    );
    return;
  }

  const nextLead = {
    ...prevLead,
    ...updateData,
    lead: {
      ...prevLead?.lead,
      ...updateData?.lead,
    },
    location:
      updateData?.location !== undefined
        ? updateData.location
        : prevLead?.location ?? null,
    contact:
      updateData?.contact !== undefined
        ? updateData.contact
        : prevLead?.contact ?? null,
    projects:
      updateData?.projects !== undefined
        ? updateData.projects
        : prevLead?.projects ?? null,
  };

  yield put({ type: types.ADD_LEAD, payload: { id: leadId, data: nextLead } });

  try {
    let response;

    if (emailUpdate) {
      response = yield call(
        leadsApi.updateLeadContactEmail,
        orgId,
        leadId,
        emailUpdate.emailId,
        {
          field_value: emailUpdate.fieldValue,
          ap_label_id: emailUpdate.contactType,
          field_label: emailUpdate.fieldLabel,
        },
      );
      if (!emailUpdate.emailId && response.ok) {
        const data = objToCamelCase(response.data);
        const newEmails = nextLead.contact.emails.map(email => email.isNew ? data : email);
        yield put({
          type: types.ADD_LEAD,
          payload: {
            id: leadId,
            data: {
              ...nextLead,
              contact: {
                ...nextLead.contact,
                emails: newEmails,
              },
            },
          },
        });
      }
    }
    else if (phoneUpdate) {
      response = yield call(
        leadsApi.updateLeadContactPhone,
        orgId,
        leadId,
        phoneUpdate.phoneId,
        {
          field_value: phoneUpdate.fieldValue,
          ap_label_id: phoneUpdate.contactType,
          field_label: phoneUpdate.fieldLabel,
        },
      );
      if (!phoneUpdate.phoneId && response.ok) {
        const data = objToCamelCase(response.data);
        const newPhones = nextLead.contact.phones.map(phone => phone.isNew ? data : phone);
        yield put({
          type: types.ADD_LEAD,
          payload: {
            id: leadId,
            data: {
              ...nextLead,
              contact: {
                ...nextLead.contact,
                phones: newPhones,
              },
            },
          },
        });
      }
    }
    else if (addressUpdate) {
      const { addressId, contactType, fieldLabel, ...addressFields } = addressUpdate;
      response = yield call(
        leadsApi.updateLeadContactAddress,
        orgId,
        leadId,
        addressId,
        {
          ...objToSnake(addressFields),
          ap_label_id: contactType,
          field_label: fieldLabel,
        },
      );
      // Add the newly created id to the address.
      if (!addressId && response.ok) {
        const data = objToCamelCase(response.data);
        const newAddresses = nextLead.contact.addresses.map(address => address.isNew ? data : address);
        yield put({
          type: types.ADD_LEAD,
          payload: {
            id: leadId,
            data: {
              ...nextLead,
              contact: {
                ...nextLead.contact,
                addresses: newAddresses,
              },
            },
          },
        });
      }
    }
    else if (websiteUpdate) {
      const { websiteId, ...websiteFields } = websiteUpdate;
      response = yield call(
        leadsApi.updateLeadContactWebsite,
        orgId,
        leadId,
        websiteId,
        {
          field_value: websiteFields.fieldValue,
          ap_label_id: websiteFields.contactType,
          field_label:  websiteFields.fieldLabel,
        },
      );
      if (!websiteId && response.ok) {
        const data = objToCamelCase(response.data);
        const newWebsites = nextLead.contact.websites.map(website => website.isNew ? data : website);
        yield put({
          type: types.ADD_LEAD,
          payload: {
            id: leadId,
            data: {
              ...nextLead,
              contact: {
                ...nextLead.contact,
                websites: newWebsites,
              },
            },
          },
        });
      }
    }
    else {
      response = yield call(
        leadsApi.updateLead,
        orgId,
        leadId,
        prepareLeadForApi(nextLead),
      );
    }

    if (!response.ok) {
      yield put({
        type: types.ADD_LEAD,
        payload: { id: leadId, data: prevLead },
      });
      if (response.data.INVALID_PHONE_FORMAT) {
        yield put(
          pushSiteError({
            message: 'Please review and provide a valid phone number.',
          }),
        );
      }
      else {
        yield put(
          pushSiteError({
            message: websiteUpdate
              ? 'Please review and update your website format.'
              : "Sorry, we couldn't update your lead. Please try again.",
          }),
        );
      }
    }
  } catch (err) {
    yield put({
      type: types.ADD_LEAD,
      payload: { id: leadId, data: prevLead },
    });
    yield put(
      pushSiteError({
        message: "Sorry, we couldn't update your lead. Please try again.",
      }),
    );
  }
}

function* deleteLead(action) {
  const { id } = action.payload || {};
  if (!id) return;

  const leadToDelete = yield select(state => state.leads.leadsById?.[id]);
  if (!leadToDelete) {
    // eslint-disable-next-line no-console
    console.log(`[deleteLead saga] Lead with id=${id} not found in state.leadsById, aborting delete.`);
    yield put(
      pushSiteError({
        message: "Sorry, we couldn't delete your lead. Please try again.",
      }),
    );

    return;
  }

  try {
    const { data } = yield call(apollo.deleteLead, { id: parseInt(id, 10) });
    const isDeleted = data?.deleteLead?.isDeleted;

    if (data && isDeleted) {
      yield put({ type: types.DELETE_LEAD_SUCCESS, payload: { id } });
      yield put({ type: DELETE_LEAD_SUCCESS, payload: { id } });
    } else {
      yield put({
        type: types.ADD_LEAD,
        payload: { id, data: leadToDelete },
      });
      yield put(
        pushSiteError({
          message: "Sorry, we couldn't delete your lead. Please try again.",
        }),
      );
    }
  } catch (err) {
    yield put({
      type: types.ADD_LEAD,
      payload: { id, data: leadToDelete },
    });
    yield put(
      pushSiteError({
        message: "Sorry, we couldn't delete your lead. Please try again.",
      }),
    );
  }
};

function* deleteLeadContactData(action) {
  const { orgId, leadId, contactId, type } = action.payload || {};

  try {
    const lead = yield select(state => state.leads.leadsById?.[leadId]);
    const oldLead = { ...lead };
    const contactData = lead.contact[type];
    const newContactData = [ ...contactData.filter(contact => contact.id !== contactId)];
    const newLead = {
      ...lead,
      contact: {
        ...lead.contact,
        [type]: newContactData,
      },
    };
    yield put({
      type: types.ADD_LEAD,
      payload: {
        id: leadId,
        data: newLead,
      },
    });
    const response = yield call(leadRecordApi.deleteLeadContact, orgId, leadId, contactId, type);

    if (!response.ok) {
      yield put({
        type: types.ADD_LEAD,
        payload: {
          id: leadId,
          data: oldLead,
        },
      });
      yield put(pushSiteError({
        message: "Sorry, we couldn't delete that contact info. Please try again.",
      }));
    }
  } catch {
    yield put(pushSiteError({
      message: "Sorry, we couldn't delete that contact info. Please try again.",
    }));
  }
};

const PRIMARY_CONTACT_KEY_MAP = {
  emails: 'primary_email_id',
  phones: 'primary_phone_id',
  addresses: 'primary_address_id',
  websites: 'primary_website_id',
};

const PRIMARY_CONTACT_ID_MAP = {
  emails: 'primaryEmailId',
  phones: 'primaryPhoneId',
  addresses: 'primaryAddressId',
  websites: 'primaryWebsiteId',
};

function* setPrimaryContact(action) {
  const { orgId, leadId, contactId, contactType } = action.payload || {};

  try {
    const lead = yield select(state => state.leads.leadsById?.[leadId]);
    const oldLead = { ...lead };
    const contactData = lead.contact[contactType];
    const itemIndex = contactData.findIndex(c => c.id === contactId);

    if (itemIndex <= 0) return;

    const reordered = [
      contactData[itemIndex],
      ...contactData.slice(0, itemIndex),
      ...contactData.slice(itemIndex + 1),
    ];

    const payload = {
      primary_address_id: lead.contact?.primaryAddressId,
      primary_email_id: lead.contact?.primaryEmailId,
      primary_phone_id: lead.contact?.primaryPhoneId,
      primary_website_id: lead.contact?.primaryWebsiteId,
    };

    const primaryIdKey = PRIMARY_CONTACT_ID_MAP[contactType];
    const newLead = {
      ...lead,
      contact: {
        ...lead.contact,
        [contactType]: reordered,
        [primaryIdKey]: contactId,
      },
    };
    yield put({
      type: types.ADD_LEAD,
      payload: { id: leadId, data: newLead },
    });

    const apiKey = PRIMARY_CONTACT_KEY_MAP[contactType];
    const response = yield call(leadsApi.updateLeadPrimaryContact, orgId, leadId, { ...payload, [apiKey]: contactId });

    if (!response.ok) {
      yield put({
        type: types.ADD_LEAD,
        payload: { id: leadId, data: oldLead },
      });
      yield put(pushSiteError({
        message: "Sorry, we couldn't update the primary contact. Please try again.",
      }));
    }
  } catch {
    yield put(pushSiteError({
      message: "Sorry, we couldn't update the primary contact. Please try again.",
    }));
  }
}

function* getLeadComments(action) {
  const { orgId, leadId } = action.payload;
  try {
    const response = yield call(leadRecordApi.getLeadComments, orgId, leadId);

    if (response.status === 404) {
      // exit early because the getLead saga will handle.
      return;
    }
    if (response.ok) {
      yield put({
        type: types.ADD_LEAD_COMMENTS,
        payload: { id: leadId, data: objToCamelCase(response.data) },
      });
    } else {
      yield put(pushSiteError({
        message: "Sorry, we couldn't get your lead activity. Please refresh and try again.",
      }));
    }
  } catch (err) {
    yield put(pushSiteError({
      message: "Sorry, we couldn't get your lead activity. Please refresh and try again.",
      error: err,
    }));
  }
};

function* createLeadComment(action) {
  const { orgId, leadId, comment } = action.payload;

  if (!orgId || !leadId || !comment) {
    yield put(pushSiteError({
      message: "Sorry, we couldn't update your comment. Please try again.",
    }));
    return;
  }

  try {
    const lead = yield select(state => state.leads.leadsById[leadId]);
    const response = yield call(leadRecordApi.createLeadComment, orgId, leadId, comment);
    if (response.ok) {
      yield put({
        type: types.ADD_LEAD_COMMENTS,
        payload: { id: leadId, data: [...lead.comments, objToCamelCase(response.data)] },
      });
    } else {
      yield put(pushSiteError({
        message: "Sorry, we couldn't create your comment. Please try again.",
      }));
    }
  } catch (err) {
    yield put(pushSiteError({
      message: "Sorry, we couldn't create your comment. Please try again.",
      error: err,
    }));
  }
};

function* updateLeadComment(action) {
  const { orgId, leadId, commentId, comment } = action.payload;

  if (!orgId || !leadId || !commentId || !comment) {
    yield put(pushSiteError({
      message: "Sorry, we couldn't update your comment. Please try again.",
    }));
    return;
  }
  const lead = yield select(state => state.leads.leadsById[leadId]);
  if (!lead) {
    yield put(pushSiteError({
      message: "Sorry, we couldn't update your comment. Please try again.",
    }));
    return;
  }
  const oldComments = [...lead.comments];
  const newComments = [...lead.comments];
  const newCommentIndex = lead.comments.findIndex(_comment => _comment.id === commentId);
  if (newCommentIndex >= 0) newComments[newCommentIndex].body = comment;
  yield put({
    type: types.ADD_LEAD_COMMENTS,
    payload: { id: leadId, data: newComments },
  });
  try {
    const response = yield call(leadRecordApi.updateLeadComment, orgId, leadId, commentId, comment);
    if (!response.ok) {
      yield put({
        type: types.ADD_LEAD_COMMENTS,
        payload: { id: leadId, data: oldComments },
      });
      yield put(pushSiteError({
        message: "Sorry, we couldn't update your comment. Please try again.",
      }));
    }
  } catch (err) {
    yield put(pushSiteError({
      message: "Sorry, we couldn't update your comment. Please try again.",
      error: err,
    }));
  }
};

function* deleteLeadComment(action) {
  const { orgId, leadId, commentId } = action.payload;

  if (!orgId || !leadId || !commentId) {
    yield put(pushSiteError({
      message: "Sorry, we couldn't delete your comment. Please try again.",
    }));
    return;
  }
  const lead = yield select(state => state.leads.leadsById[leadId]);
  if (!lead) {
    yield put(pushSiteError({
      message: "Sorry, we couldn't delete your comment. Please try again.",
    }));
    return;
  }
  const oldComments = [...lead.comments];
  const newComments = lead.comments.filter(comment => comment.id !== commentId);
  yield put({
    type: types.ADD_LEAD_COMMENTS,
    payload: { id: leadId, data: newComments },
  });
  try {
    const response = yield call(leadRecordApi.deleteLeadComment, orgId, leadId, commentId);
    if (!response.ok) {
      yield put({
        type: types.ADD_LEAD_COMMENTS,
        payload: { id: leadId, data: oldComments },
      });
      yield put(pushSiteError({
        message: "Sorry, we couldn't update your comment. Please try again.",
      }));
    }
  } catch (err) {
    yield put(pushSiteError({
      message: "Sorry, we couldn't update your comment. Please try again.",
      error: err,
    }));
  }
};

function* getChecklistItems(action) {
  try {
    const { orgId, leadId } = action.payload || {};
    if (!orgId || !leadId) return;

    const { data, ok, status } = yield call(
      leadsApi.fetchChecklistItems,
      orgId,
      leadId,
    );
    if (status === 404) {
      // exit early because the getLead saga will handle.
      return;
    }
    if (ok && data) {
      const camel = objToCamelCase(data);
      yield put({
        type: types.UPDATE_CHECKLIST_ITEM_STORE,
        payload: { id: leadId, op: 'LOAD', data: camel },
      });
    } else {
      yield put(
        pushSiteError({
          message:
            "Sorry, we couldn't load your lead checklist. Please try again.",
        }),
      );
    }
  } catch (err) {
    yield put(
      pushSiteError({
        message:
          "Sorry, we couldn't load your lead checklist. Please try again.",
      }),
    );
  }
}

function* createChecklistFromTemplate(action) {
  const { orgId, leadId, templateId } = action.payload;
  if (!orgId || !leadId) {
    yield put(
      pushSiteError({
        message: "Sorry, we couldn't find your organization or lead to create your checklist. Please try again.",
      }),
    );
    return;
  }
  if (!templateId) {
    yield put(
      pushSiteError({
        message: "Sorry, we couldn't find your template to create your checklist. Please try again.",
      }),
    );
    return;
  }
  try {
    const response = yield call(leadsApi.createLeadChecklistFromTemplate, orgId, leadId, templateId);
    if (response.ok && response.data) {
      const checklistItems = objToCamelCase(response.data);
      yield put({
        type: types.UPDATE_CHECKLIST_ITEM_STORE,
        payload: { id: leadId, op: 'LOAD', data: checklistItems },
      });
    } else {
      yield put(
        pushSiteError({ message: "Sorry, we couldn't create your checklist. Please try again." }),
      );
    }
  } catch (err) {
    yield put(
      pushSiteError({
        message: "Sorry, we couldn't create your checklist. Please try again.",
        error: err,
      }),
    );
  }
};

/**
 * Calls api to the checklist item and handle the redux side effects.
 * @param {*} action - the redux dispatch object.
 * @param {number} action.orgId - The org id to add checklist item to
 * @param {number} action.leadId - The lead id to attach the checklist item to.
 * @param {number} action.referenceId - An id of another checklist item if the
 *                                      new item should be ordered after it.
 */
function* createChecklistItem(action) {
  const { orgId, leadId, referenceId } = action.payload || {};
  if (!orgId || !leadId) return;

  const lead = yield select((state) => state.leads.leadsById?.[leadId]);
  const snapshotChecklists = {
    items: lead?.checklists?.items || [],
    uncategorized: lead?.checklists?.uncategorized || [],
  };

  try {
    const { ok, data } = yield call(
      leadsApi.createChecklistItem,
      orgId,
      leadId,
      referenceId,
    );
    if (ok && data) {
      yield put({
        type: types.UPDATE_CHECKLIST_ITEM_STORE,
        payload: {
          id: leadId,
          op: 'CREATE_SUCCESS',
          item: objToCamelCase(data.item),
          changedItems: objToCamelCase(data.changed_items),
        },
      });
    } else {
      yield put({
        type: types.UPDATE_CHECKLIST_ITEM_STORE,
        payload: { id: leadId, op: 'REVERT', snapshotChecklists },
      });
      yield put(
        pushSiteError({
          message:
            "Sorry, we couldn't create your lead checklist item. Please try again.",
        }),
      );
    }
  } catch (err) {
      yield put({
        type: types.UPDATE_CHECKLIST_ITEM_STORE,
        payload: { id: leadId, op: 'REVERT', snapshotChecklists },
      });
    yield put(
      pushSiteError({
        message:
          "Sorry, we couldn't create your lead checklist item. Please try again.",
      }),
    );
  }
}

function* updateChecklistItem(action) {
  const { orgId, leadId, checklistItemId, updateData } = action.payload || {};
  if (!orgId || !leadId || !checklistItemId || !updateData) return;

  const lead = yield select((state) => state.leads.leadsById?.[leadId]);
  const items = lead?.checklists?.items || [];
  const item = items.find((it) => it.id === checklistItemId);
  if (!item) {
    yield put(
      pushSiteError({
        message:
          "Sorry, we couldn't update your lead checklist item. Please try again.",
      }),
    );
    return;
  }

  const snapshotChecklists = {
    items,
  };

  yield put({
    type: types.UPDATE_CHECKLIST_ITEM_STORE,
    payload: {
      id: leadId,
      op: 'UPDATE',
      checklistItemId,
      updateData,
    },
  });

  try {
    const { ok, data } = yield call(
      leadsApi.updateChecklistItem,
      orgId,
      leadId,
      checklistItemId,
      prepareChecklistItemForApi({ ...item, ...updateData }),
    );

    if (ok) {
      // fetch default reminders
      const currentReminders = lead?.checklists?.reminders?.[checklistItemId] || [];
      if (updateData.dueDate && currentReminders.length === 0) {
        yield put({
          type: types.GET_CHECKLIST_REMINDERS,
          payload: { orgId, leadId, taskId: checklistItemId },
        });
      }

      if (data?.item) {
        yield put({
          type: types.UPDATE_CHECKLIST_ITEM_STORE,
          payload: {
            id: leadId,
            op: 'UPDATE_SUCCESS',
            checklistItemId,
            item: objToCamelCase(data.item),
          },
        });
      }
    } else {
      yield put({
        type: types.UPDATE_CHECKLIST_ITEM_STORE,
        payload: { id: leadId, op: 'REVERT', snapshotChecklists },
      });
      yield put(
        pushSiteError({
          message:
            "Sorry, we couldn't update your lead checklist item. Please try again.",
        }),
      );
    }
  } catch (err) {
      yield put({
        type: types.UPDATE_CHECKLIST_ITEM_STORE,
        payload: { id: leadId, op: 'REVERT', snapshotChecklists },
      });
    yield put(
      pushSiteError({
        message:
          "Sorry, we couldn't update your lead checklist item. Please try again.",
      }),
    );
  }
}

function* deleteChecklistItem(action) {
  const { orgId, leadId, checklistItemId } = action.payload || {};
  if (!orgId || !leadId || !checklistItemId) return;

  const lead = yield select((state) => state.leads.leadsById?.[leadId]);
  const snapshotChecklists = {
    items: lead?.checklists?.items || [],
    uncategorized: lead?.checklists?.uncategorized || [],
    commentCounts: lead?.checklists?.commentCounts || {},
    assignmentCount: lead?.checklists?.assignmentCount || 0,
    assignments: lead?.checklists?.assignments || {},
    comments: lead?.checklists?.comments || [],
  };

  yield put({
    type: types.REMOVE_CHECKLIST_ITEM_STORE,
    payload: { id: leadId, op: 'DELETE', checklistItemId },
  });

  try {
    const { ok } = yield call(
      leadsApi.deleteChecklistItem,
      orgId,
      leadId,
      checklistItemId,
    );
    if (!ok) {
      yield put({
        type: types.UPDATE_CHECKLIST_ITEM_STORE,
        payload: { id: leadId, op: 'REVERT', snapshotChecklists },
      });
      yield put(
        pushSiteError({
          message:
            "Sorry, we couldn't delete your lead checklist item. Please try again.",
        }),
      );
    } else {
      // Handle removing this task from other areas of the app.
      const events = yield select((state) => state.events.events);
      if (events.legnth > 0) {
        const leadTaskEvent = events[checklistItemId];
        if (leadTaskEvent) {
          yield put({
            type: REMOVE_EVENT,
            payload: { id: checklistItemId },
          });
        }
        yield put({
          type: DELETE_TASK_SUCCESS,
          payload: { deleted: true, taskId: checklistItemId },
        });
      }
    }
  } catch (err) {
    yield put({
      type: types.UPDATE_CHECKLIST_ITEM_STORE,
      payload: { id: leadId, op: 'REVERT', snapshotChecklists },
    });
    yield put(
      pushSiteError({
        message:
          "Sorry, we couldn't delete your lead checklist item. Please try again.",
      }),
    );
  }
}

function* addChecklistAssignment(action) {
  const { orgId, leadId, loginId, checklistItemId } = action.payload || {};
  if (!orgId || !leadId || !loginId || !checklistItemId) return;

  const lead = yield select((state) => state.leads.leadsById?.[leadId]);
  const items = lead?.checklists?.items || [];
  const item = items.find((it) => it.id === checklistItemId);
  if (!item) {
    yield put(
      pushSiteError({
        message:
          "Sorry, we couldn't add your lead checklist item assignment. Please try again.",
      }),
    );
    return;
  }

  const snapshotChecklists = {
    assignments: lead?.checklists?.assignments || {},
    assignmentCount: lead?.checklists?.assignmentCount || 0,
  };

  yield put({
    type: types.UPDATE_CHECKLIST_ASSIGNMENT_STORE,
    payload: {
      id: leadId,
      op: 'CREATE',
      loginId,
      checklistItemId,
    },
  });

  try {
    const { ok, data } = yield call(
      leadsApi.addChecklistAssignment,
      orgId,
      leadId,
      loginId,
      checklistItemId,
    );
    if (ok && data) {
      yield put({
        type: types.UPDATE_CHECKLIST_ASSIGNMENT_STORE,
        payload: {
          id: leadId,
          op: 'CREATE_SUCCESS',
          loginId,
          assignment: objToCamelCase(data.assignment),
        },
      });
    } else {
      yield put({
        type: types.UPDATE_CHECKLIST_ASSIGNMENT_STORE,
        payload: { id: leadId, op: 'REVERT', snapshotChecklists },
      });
      yield put(
        pushSiteError({
          message:
            "Sorry, we couldn't add your lead checklist item assignment. Please try again.",
        }),
      );
    }
  } catch (err) {
    yield put({
      type: types.UPDATE_CHECKLIST_ASSIGNMENT_STORE,
      payload: { id: leadId, op: 'REVERT', snapshotChecklists },
    });
    yield put(
      pushSiteError({
        message:
          "Sorry, we couldn't add your lead checklist item assignment. Please try again.",
      }),
    );
  }
}

function* removeChecklistAssignment(action) {
  const { orgId, leadId, loginId, checklistItemId } = action.payload || {};
  if (!orgId || !leadId || !loginId || !checklistItemId) return;

  const lead = yield select((state) => state.leads.leadsById?.[leadId]);
  if (!lead) {
    yield put(
      pushSiteError({
        message:
          "Sorry, we couldn't remove your lead checklist item assignment. Please try again.",
      }),
    );
    return;
  }

  const snapshotChecklists = {
    assignments: lead?.checklists?.assignments || {},
    assignmentCount: lead?.checklists?.assignmentCount || 0,
  };

  yield put({
    type: types.REMOVE_CHECKLIST_ASSIGNMENT_STORE,
    payload: {
      id: leadId,
      op: 'DELETE',
      loginId,
      checklistItemId,
    },
  });

  try {
    const { ok } = yield call(
      leadsApi.removeChecklistAssignment,
      orgId,
      leadId,
      loginId,
      checklistItemId,
    );
    if (!ok) {
      yield put({
        type: types.UPDATE_CHECKLIST_ASSIGNMENT_STORE,
        payload: { id: leadId, op: 'REVERT', snapshotChecklists },
      });
      yield put(
        pushSiteError({
          message:
            "Sorry, we couldn't remove your lead checklist item assignment. Please try again.",
        }),
      );
    }
  } catch (err) {
    yield put({
      type: types.UPDATE_CHECKLIST_ASSIGNMENT_STORE,
      payload: { id: leadId, op: 'REVERT', snapshotChecklists },
    });
    yield put(
      pushSiteError({
        message:
          "Sorry, we couldn't remove your lead checklist item assignment. Please try again.",
      }),
    );
  }
}

function* getChecklistComments(action) {
  try {
    const { orgId, leadId, checklistItemId } = action.payload || {};
    if (!orgId || !leadId || !checklistItemId) return;

    const { data, ok } = yield call(
      leadsApi.getChecklistComments,
      orgId,
      leadId,
      checklistItemId,
    );
    if (ok && data) {
      const camel = objToCamelCase(data);
      yield put({
        type: types.UPDATE_CHECKLIST_COMMENT_STORE,
        payload: { id: leadId, checklistItemId, op: 'LOAD', data: camel },
      });
    } else {
      yield put(
        pushSiteError({
          message:
            "Sorry, we couldn't load your lead checklist comments. Please try again.",
        }),
      );
    }
  } catch (err) {
    yield put(
      pushSiteError({
        message:
          "Sorry, we couldn't load your lead checklist comments. Please try again.",
      }),
    );
  }
}

function* addChecklistComment(action) {
  const { orgId, leadId, checklistItemId, comment } = action.payload || {};
  if (!orgId || !leadId || !checklistItemId || !comment) return;

  const lead = yield select((state) => state.leads.leadsById?.[leadId]);
  const items = lead?.checklists?.items || [];
  const item = items.find((it) => it.id === checklistItemId);
  if (!item) {
    yield put(
      pushSiteError({
        message:
          "Sorry, we couldn't create your lead checklist item comment. Please try again.",
      }),
    );
    return;
  }

  const tempId = `tmp_${Date.now()}`;
  const tempDate = dayjs.utc().format('YYYY-MM-DDTHH:mm:ss.SSS000ZZ');

  const snapshotChecklists = {
    comments: lead?.checklists?.comments || [],
    commentCounts: lead?.checklists?.commentCounts || {},
  };

  yield put({
    type: types.UPDATE_CHECKLIST_COMMENT_STORE,
    payload: {
      id: leadId,
      op: 'CREATE',
      checklistItemId,
      tempId,
      tempDate,
      comment,
      discussionId: item?.discussionId,
    },
  });

  try {
    const { ok, data } = yield call(
      leadsApi.addChecklistComment,
      orgId,
      leadId,
      checklistItemId,
      comment,
    );
    if (ok && data) {
      const createdComment = objToCamelCase(data);
      yield put({
        type: types.UPDATE_CHECKLIST_COMMENT_STORE,
        payload: {
          id: leadId,
          op: 'CREATE_SUCCESS',
          tempId,
          comment: createdComment,
          checklistItemId,
        },
      });
    } else {
      yield put({
        type: types.UPDATE_CHECKLIST_COMMENT_STORE,
        payload: { id: leadId, op: 'REVERT', snapshotChecklists },
      });
      yield put(
        pushSiteError({
          message:
            "Sorry, we couldn't create your lead checklist item comment. Please try again.",
        }),
      );
    }
  } catch (err) {
    yield put({
      type: types.UPDATE_CHECKLIST_COMMENT_STORE,
      payload: { id: leadId, op: 'REVERT', snapshotChecklists },
    });
    yield put(
      pushSiteError({
        message:
          "Sorry, we couldn't create your lead checklist item comment. Please try again.",
      }),
    );
  }
}

function* updateChecklistComment(action) {
  const { orgId, leadId, checklistItemId, commentId, comment } =
    action.payload || {};
  if (!orgId || !leadId || !checklistItemId || !commentId || !comment) return;
  const lead = yield select((state) => state.leads.leadsById?.[leadId]);
  const comments = lead?.checklists?.comments || [];
  if (!comments[checklistItemId]) {
  yield put(
      pushSiteError({
        message:
          "Sorry, we couldn't update your lead checklist item comment. That comment might be deleted.",
      }),
    );
  }
  const existsComment = comments[checklistItemId].some((c) => c.id === commentId);
  if (!existsComment) {
    yield put(
      pushSiteError({
        message:
          "Sorry, we couldn't update your lead checklist item comment. Please try again.",
      }),
    );
    return;
  }

  const snapshotChecklists = { comments };
  const updatedAt = dayjs.utc().format('YYYY-MM-DDTHH:mm:ss.SSS000ZZ');

  yield put({
    type: types.UPDATE_CHECKLIST_COMMENT_STORE,
    payload: {
      id: leadId,
      op: 'UPDATE',
      commentId,
      comment,
      updatedAt,
      checklistItemId,
    },
  });

  try {
    const { ok } = yield call(
      leadsApi.editChecklistComment,
      orgId,
      leadId,
      checklistItemId,
      commentId,
      comment,
    );
    if (!ok) {
      yield put({
        type: types.UPDATE_CHECKLIST_COMMENT_STORE,
        payload: { id: leadId, op: 'REVERT', snapshotChecklists },
      });
      yield put(
        pushSiteError({
          message:
            "Sorry, we couldn't update your lead checklist item comment. Please try again.",
        }),
      );
    }
  } catch (err) {
    yield put({
      type: types.UPDATE_CHECKLIST_COMMENT_STORE,
      payload: { id: leadId, op: 'REVERT', snapshotChecklists },
    });
    yield put(
      pushSiteError({
        message:
          "Sorry, we couldn't update your lead checklist item comment. Please try again.",
      }),
    );
  }
}

function* deleteChecklistComment(action) {
  const { orgId, leadId, checklistItemId, commentId } = action.payload || {};
  if (!orgId || !leadId || !checklistItemId || !commentId) return;

  const lead = yield select((state) => state.leads.leadsById?.[leadId]);
  const comments = lead?.checklists?.comments || {};
  if (!comments[checklistItemId] || !Array.isArray(comments[checklistItemId])) {
    yield put(
      pushSiteError({
        message:
          "We couldn't delete your comment. That comment might already be deleted",
      }),
    );
  }
  const existsComment = comments[checklistItemId].some((c) => c.id === commentId);
  if (!existsComment) {
    yield put(
      pushSiteError({
        message:
          "Sorry, we couldn't update your lead checklist item comment. Please try again.",
      }),
    );
    return;
  }

  const snapshotChecklists = {
    comments,
    commentCounts: lead?.checklists?.commentCounts || {},
  };

  yield put({
    type: types.REMOVE_CHECKLIST_COMMENT_STORE,
    payload: {
      id: leadId,
      op: 'DELETE',
      checklistItemId,
      commentId,
    },
  });

  try {
    const { ok } = yield call(
      leadsApi.deleteChecklistComment,
      orgId,
      leadId,
      checklistItemId,
      commentId,
    );
    if (!ok) {
      yield put({
        type: types.UPDATE_CHECKLIST_COMMENT_STORE,
        payload: { id: leadId, op: 'REVERT', snapshotChecklists },
      });
      yield put(
        pushSiteError({
          message:
            "Sorry, we couldn't delete your lead checklist item comment. Please try again.",
        }),
      );
    }
  } catch (err) {
    yield put({
      type: types.UPDATE_CHECKLIST_COMMENT_STORE,
      payload: { id: leadId, op: 'REVERT', snapshotChecklists },
    });
    yield put(
      pushSiteError({
        message:
          "Sorry, we couldn't delete your lead checklist item comment. Please try again.",
      }),
    );
  }
}

function* deleteQuote(action) {
  const { orgId, quoteId, leadId } = action.payload || {};

  if (!orgId || !quoteId) {
    yield put(
      pushSiteError({
        message: "Sorry, we couldn't delete the quote. Missing required data.",
      }),
    );
    return;
  }

  const prevLead = yield select(state => state.leads.leadsById?.[leadId]);

  if (leadId && prevLead) {
    yield put({
      type: types.ADD_LEAD,
      payload: {
        id: leadId,
        data: {
          ...prevLead,
          quotes: prevLead.quotes.filter(quote => quote.id !== quoteId),
        },
      },
    });
  }

  try {
    const { ok } = yield call(leadsApi.deleteQuote, orgId, quoteId);

    if (!ok) {
      if (leadId && prevLead) {
        yield put({
          type: types.ADD_LEAD,
          payload: { id: leadId, data: prevLead },
        });
      }

      yield put(
        pushSiteError({
          message: "Sorry, we couldn't delete the quote. Please try again.",
        }),
      );
    }
  } catch (err) {
    if (leadId && prevLead) {
      yield put({
        type: types.ADD_LEAD,
        payload: { id: leadId, data: prevLead },
      });
    }

    yield put(
      pushSiteError({
        message: "Sorry, we couldn't delete the quote. Please try again.",
      }),
    );
  }
}

function* deleteInvoice(action) {
  const { orgId, invoiceId, leadId } = action.payload || {};

  if (!orgId || !invoiceId) {
    yield put(
      pushSiteError({
        message: "Sorry, we couldn't delete the invoice. Missing required data.",
      }),
    );
    return;
  }

  const prevLead = yield select(state => state.leads.leadsById?.[leadId]);

  if (leadId && prevLead) {
    yield put({
      type: types.ADD_LEAD,
      payload: {
        id: leadId,
        data: {
          ...prevLead,
          invoices: prevLead.invoices.filter(invoice => invoice.id !== invoiceId),
        },
      },
    });
  }

  try {
    const { ok } = yield call(leadsApi.deleteInvoice, orgId, invoiceId);

    if (!ok) {
      if (leadId && prevLead) {
        yield put({
          type: types.ADD_LEAD,
          payload: { id: leadId, data: prevLead },
        });
      }

      yield put(
        pushSiteError({
          message: "Sorry, we couldn't delete the invoice. Please try again.",
        }),
      );
    }
  } catch (err) {
    if (leadId && prevLead) {
      yield put({
        type: types.ADD_LEAD,
        payload: { id: leadId, data: prevLead },
      });
    }

    yield put(
      pushSiteError({
        message: "Sorry, we couldn't delete the invoice. Please try again.",
      }),
    );
  }
}

function* deleteDocument(action) {
  const { orgId, documentId, leadId } = action.payload || {};

  if (!orgId || !documentId) {
    yield put(
      pushSiteError({
        message: "Sorry, we couldn't delete the document. Missing required data.",
      }),
    );
    return;
  }

  const prevLead = yield select(state => state.leads.leadsById?.[leadId]);

  if (leadId && prevLead) {
    yield put({
      type: types.ADD_LEAD,
      payload: {
        id: leadId,
        data: {
          ...prevLead,
          documents: prevLead.documents.filter(document => document.id !== documentId),
        },
      },
    });
  }

  try {
    const { ok } = yield call(deleteDocumentApi, orgId, documentId);

    if (!ok) {
      if (leadId && prevLead) {
        yield put({
          type: types.ADD_LEAD,
          payload: { id: leadId, data: prevLead },
        });
      }

      yield put(
        pushSiteError({
          message: "Sorry, we couldn't delete the document. Please try again.",
        }),
      );
    }
  } catch (err) {
    if (leadId && prevLead) {
      yield put({
        type: types.ADD_LEAD,
        payload: { id: leadId, data: prevLead },
      });
    }

    yield put(
      pushSiteError({
        message: "Sorry, we couldn't delete the document. Please try again.",
      }),
    );
  }
}

function* updateCustomFormField(action) {
  const errMessage = "Sorry, we couldn't update your lead. Please try again.";
  const { orgId, leadId, id, urlId, fieldState } = action.payload || {};

  if (!leadId || !id || !fieldState) {
    yield put(pushSiteError({ message: errMessage }));
  }
  const userDefinedFields = yield select(state => state.leads.leadCustomFields[leadId]);

  if (!userDefinedFields) {
    // eslint-disable-next-line no-console
    console.log(
      `[updateLead saga] custom fields with id=${leadId} not found in state.leads.leadCustomFields, aborting update.`,
    );
    yield put(pushSiteError({ message: errMessage }));
    return;
  }

  const originalUserDefinedFields = [...userDefinedFields];
  const newUserDefinedFields = [...userDefinedFields];
  const foundIndex = newUserDefinedFields.findIndex(field => field.id === parseInt(id, 10));
  if (foundIndex >= 0) {
    newUserDefinedFields[foundIndex] = fieldState;
    yield put({
      type: types.ADD_CUSTOM_FORM_FIELD_DATA,
      payload: {
        id: leadId,
        data: newUserDefinedFields,
      },
    });
  }
  try {
    const response = yield call(
      leadRecordApi.updateLeadCustomFieldResponse, orgId, leadId, id, urlId, fieldState,
    );
    let hasError = false;
    if (response.ok) {
      const { data } = response;
      // It's possible the fields have the same order id, so we update the array
      // by index so the display order doesn't change when saving a user defined field.
      Object.keys(data).forEach(responseId => {
        const _foundIndex = newUserDefinedFields.findIndex(field => {
          return field.id === parseInt(responseId, 10);
        });
        // We're just validating that the server state is equal to our current state.
        const convertedResponse = objToCamelCase(data[responseId]);
        if (_foundIndex >= 0 && !isEqual(newUserDefinedFields[_foundIndex], convertedResponse)) {
          hasError = true;
        }
      });

      if (hasError) {
        // Revert
        yield put({
          type: types.ADD_CUSTOM_FORM_FIELD_DATA,
          payload: {
            id: leadId,
            data: originalUserDefinedFields,
          },
        });
        yield put(pushSiteError({ message: errMessage }));
      }
    }
  } catch (err) {
    /* eslint-disable-next-line no-console */
    console.error(`[updateLead saga] ${err}`);
    yield put(pushSiteError({ message: errMessage }));
  }
};

function* reorderChecklistItem(action) {
  const { orgId, leadId, activeId, overId, prevItems } = action.payload || {};
  if (!orgId || !leadId || !activeId || !prevItems) return;
  if (activeId === overId) return;

  const from = prevItems.findIndex(x => x.id === activeId);
  const to = prevItems.findIndex(x => x.id === overId);
  if (
    from < 0 || to < 0 ||
    from >= prevItems.length || to >= prevItems.length
  ) return;

  const moved = arrayMove(prevItems, from, to);

  const targetOrder = prevItems[to].uncategorizedOrder;
  const nextItems = [];
  for (let i = 0; i < moved.length; i += 1) {
    const it = moved[i];
    const old = it.uncategorizedOrder ?? 0;

    if (to < from) {
      // moving UP: the dragged item goes to position `to`,
      // all items between `to..from` shift down (+1)
      if (i === to) {
        nextItems[i] = { ...it, uncategorizedOrder: targetOrder };
      } else if (i > to && i <= from) {
        nextItems[i] = { ...it, uncategorizedOrder: old + 1 };
      } else {
        nextItems[i] = it;
      }
    } else if (to > from) {
      // moving DOWN: the dragged item goes to position `to`,
      // all items between `from..to` shift up (-1)
      if (i === to) {
        nextItems[i] = { ...it, uncategorizedOrder: targetOrder };
      } else if (i >= from && i < to) {
        nextItems[i] = { ...it, uncategorizedOrder: old - 1 };
      } else {
        nextItems[i] = it;
      }
    }
  }

  if (nextItems.length !== prevItems.length) {
    return;
  }

  // Determine the element that comes immediately after the moved one in the newly sorted array. Its id is sent as
  // `?order_before` to the API, so that the backend can correctly insert the moved item before this element.
  // If there’s no element after (drop to the end), `overTaskId` is null
  const movedIdx = nextItems.findIndex(x => x.id === activeId);
  const overTaskId = nextItems[movedIdx + 1]?.id ?? null;

  yield put({
    type: types.RESORT_CHECKLIST_ITEMS_STORE,
    payload: { id: leadId, op: 'RESORT', items: nextItems },
  });

  const { ok, data } = yield call(leadsApi.reorderChecklistItem, orgId, leadId, activeId, overTaskId);

  const changed = data?.changed_items ?? [];
  const mismatch = changed.some(ci => {
    const found = nextItems.find(x => x.taskId === ci.task_id);

    return !found || found.uncategorizedOrder !== ci.uncategorized_order;
  });

  if (!ok || mismatch) {
    yield put(
      pushSiteError({ message: "Sorry, we couldn't reorder your tasks. Please try again in a moment." }),
    );

    yield put({
      type: types.RESORT_CHECKLIST_ITEMS_STORE,
      payload: { id: leadId, op: 'RESORT', items: prevItems },
    });
  }
};

function* getChecklistReminders(action) {
  try {
    const { orgId, leadId, taskId } = action.payload || {};
    if (!orgId || !leadId || !taskId) return;

    const { data, ok } = yield call(
      leadsApi.fetchChecklistReminders,
      orgId,
      leadId,
      taskId,
    );

    if (ok) {
      const reminders = data ? objToCamelCase(data) : [];
      yield put({
        type: types.SET_CHECKLIST_REMINDERS,
        payload: { id: leadId, taskId, reminders },
      });
    } else {
      yield put(
        pushSiteError({
          message: "Sorry, we couldn't load reminders. Please try again.",
        }),
      );
    }
  } catch (err) {
    yield put(
      pushSiteError({
        message: "Sorry, we couldn't load reminders. Please try again.",
      }),
    );
  }
}

function* createChecklistReminder(action) {
  try {
    const { orgId, leadId, taskId, input } = action.payload || {};
    if (!orgId || !leadId || !taskId || !input) return;

    const { ok } = yield call(
      leadsApi.createChecklistReminder,
      orgId,
      leadId,
      taskId,
      input,
    );

    if (ok) {
      yield put({ type: types.GET_CHECKLIST_REMINDERS, payload: { orgId, leadId, taskId } });
    } else {
      yield put(
        pushSiteError({
          message: "Sorry, we couldn't create the reminder. Please try again.",
        }),
      );
    }
  } catch (err) {
    yield put(
      pushSiteError({
        message: "Sorry, we couldn't create the reminder. Please try again.",
      }),
    );
  }
}

function* updateChecklistReminder(action) {
  try {
    const { orgId, leadId, taskId, reminderId, input } = action.payload || {};
    if (!orgId || !leadId || !taskId || !reminderId || !input) return;

    const { ok } = yield call(
      leadsApi.updateChecklistReminder,
      orgId,
      leadId,
      taskId,
      reminderId,
      input,
    );

    if (ok) {
      yield put({ type: types.GET_CHECKLIST_REMINDERS, payload: { orgId, leadId, taskId } });
    } else {
      yield put(
        pushSiteError({
          message: "Sorry, we couldn't update the reminder. Please try again.",
        }),
      );
    }
  } catch (err) {
    yield put(
      pushSiteError({
        message: "Sorry, we couldn't update the reminder. Please try again.",
      }),
    );
  }
}

function* deleteChecklistReminder(action) {
  try {
    const { orgId, leadId, taskId, reminderId } = action.payload || {};
    if (!orgId || !leadId || !taskId || !reminderId) return;

    const { ok } = yield call(
      leadsApi.deleteChecklistReminder,
      orgId,
      leadId,
      taskId,
      reminderId,
    );

    if (ok) {
      yield put({ type: types.GET_CHECKLIST_REMINDERS, payload: { orgId, leadId, taskId } });
    } else {
      yield put(
        pushSiteError({
          message: "Sorry, we couldn't delete the reminder. Please try again.",
        }),
      );
    }
  } catch (err) {
    yield put(
      pushSiteError({
        message: "Sorry, we couldn't delete the reminder. Please try again.",
      }),
    );
  }
}

export default function* watchList() {
  yield takeLatest(types.FETCH_LEAD, fetchLead);
  yield takeLatest(types.UPDATE_LEAD, updateLead);
  yield takeLatest(types.DELETE_LEAD, deleteLead);
  yield takeLatest(types.DELETE_LEAD_CONTACT_DATA, deleteLeadContactData);
  yield takeLatest(types.GET_LEAD_COMMENTS, getLeadComments);
  yield takeLatest(types.CREATE_LEAD_COMMENT, createLeadComment);
  yield takeLatest(types.UPDATE_LEAD_COMMENT, updateLeadComment);
  yield takeLatest(types.DELETE_LEAD_COMMENT, deleteLeadComment);
  yield takeLatest(types.GET_CHECKLIST_ITEMS, getChecklistItems);
  yield takeLatest(types.CREATE_CHECKLIST_FROM_TEMPLATE, createChecklistFromTemplate);
  yield takeLatest(types.CREATE_CHECKLIST_ITEM, createChecklistItem);
  yield takeLatest(types.UPDATE_CHECKLIST_ITEM, updateChecklistItem);
  yield takeLatest(types.DELETE_CHECKLIST_ITEM, deleteChecklistItem);
  yield takeLatest(types.ADD_CHECKLIST_ASSIGNMENT, addChecklistAssignment);
  yield takeLatest(types.REMOVE_CHECKLIST_ASSIGNMENT, removeChecklistAssignment);
  yield takeLatest(types.GET_CHECKLIST_COMMENTS, getChecklistComments);
  yield takeLatest(types.ADD_CHECKLIST_COMMENT, addChecklistComment);
  yield takeLatest(types.UPDATE_CHECKLIST_COMMENT, updateChecklistComment);
  yield takeLatest(types.DELETE_CHECKLIST_COMMENT, deleteChecklistComment);
  yield takeLatest(types.GET_CHECKLIST_REMINDERS, getChecklistReminders);
  yield takeLatest(types.CREATE_CHECKLIST_REMINDER, createChecklistReminder);
  yield takeLatest(types.UPDATE_CHECKLIST_REMINDER, updateChecklistReminder);
  yield takeLatest(types.DELETE_CHECKLIST_REMINDER, deleteChecklistReminder);
  yield takeLatest(types.UPDATE_CUSTOM_FORM_FIELD_DATA, updateCustomFormField);
  yield takeLatest(types.DELETE_QUOTE, deleteQuote);
  yield takeLatest(types.DELETE_INVOICE, deleteInvoice);
  yield takeLatest(types.DELETE_DOCUMENT, deleteDocument);
  yield takeLatest(types.REORDER_CHECKLIST_ITEM, reorderChecklistItem);
  yield takeLatest(types.SET_PRIMARY_CONTACT, setPrimaryContact);
}
